/**
 * 프로모션 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import { promotionService } from '../../services/promotionService';
import { Promotion, PromotionFilter } from '../../types/promotion';

const PromotionsList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<PromotionFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['promotions', filter],
    queryFn: () => promotionService.getPromotions(filter),
  });

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  const getDiscountDisplay = (promotion: Promotion) => {
    if (promotion.discount_type === 'percentage') {
      return `${promotion.discount_value}% 할인`;
    } else {
      return `${promotion.discount_value.toLocaleString()}원 할인`;
    }
  };

  const columns: Column<Promotion>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'code',
      label: '프로모션 코드',
      render: (promotion) => (
        <span
          style={{ cursor: 'pointer', color: '#00BCD4', fontWeight: 'bold' }}
          onClick={() => navigate(`/admin/promotions/${promotion.id}`)}
        >
          {promotion.code}
        </span>
      ),
    },
    {
      key: 'name',
      label: '프로모션명',
      render: (promotion) => (
        <span style={{ color: '#ccc' }}>{promotion.name}</span>
      ),
    },
    {
      key: 'discount',
      label: '할인',
      render: (promotion) => (
        <span style={{ color: '#00BCD4', fontWeight: 'bold' }}>
          {getDiscountDisplay(promotion)}
        </span>
      ),
    },
    {
      key: 'valid_from',
      label: '시작일',
      render: (promotion) => (
        <span style={{ color: '#ccc' }}>
          {new Date(promotion.valid_from).toLocaleDateString('ko-KR')}
        </span>
      ),
    },
    {
      key: 'valid_to',
      label: '종료일',
      render: (promotion) => (
        <span style={{ color: '#ccc' }}>
          {new Date(promotion.valid_to).toLocaleDateString('ko-KR')}
        </span>
      ),
    },
    {
      key: 'usage',
      label: '사용 현황',
      render: (promotion) => (
        <span style={{ color: '#ccc' }}>
          {promotion.usage_count}
          {promotion.usage_limit ? ` / ${promotion.usage_limit}` : ' / 무제한'}
        </span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (promotion) => getStatusBadge(promotion.status),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'search',
      label: '검색',
      type: 'text',
      placeholder: '코드, 프로모션명으로 검색',
    },
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'active', label: '활성' },
        { value: 'inactive', label: '비활성' },
      ],
    },
  ];

  const handleFilterChange = (values: Record<string, any>) => {
    setFilter((prev) => ({
      ...prev,
      ...values,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (promotion: Promotion) => {
    navigate(`/admin/promotions/${promotion.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>프로모션 관리</h1>
          <Button variant="primary" onClick={() => navigate('/admin/promotions/new')}>
            프로모션 등록
          </Button>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.promotions || []}
              loading={isLoading}
              emptyMessage="프로모션이 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PromotionsList;

